import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import { styled } from '@mui/material/styles';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import {
  Alert,
  AlertTitle,
  Backdrop,
  Box,
  Card,
  Container,
  Fade,
  IconButton,
  InputAdornment,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, FormikProvider, useFormik } from 'formik';
import { MotionContainer, varBounceIn } from '../animate';
import Iconify from '../Iconify';
import Page from '../Page';
import notification from '../BrowserNotifications/askPermissionNotification';
import { useWSContext } from '../../context/WebSocketContext';
import { useAuthContext } from '../../context/AuthContext';
import LoginHeader from '../LoginHeader'; // <- IMPORTA AQUI

const RootStyle = styled(Page)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    padding: theme.spacing(2, 2),
  },
}));

const urlBaseImage = process.env.REACT_APP_BASE_NAME;

const SectionStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  height: 420,
  flexDirection: 'column',
  justifyContent: 'flex-start',
  backgroundImage: `url('${urlBaseImage}/static/ilustration/banner-tecnomotor-geral-1.jpg')`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center 0px',
  marginTop: '68px',
}));

const ContentStyle = styled('div')(({ theme }) => ({
  margin: 'auto',
  maxWidth: '440px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(3, 3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(4),
  position: 'relative',
  top: '-210px',
  boxShadow: theme.shadows[20],
}));

function Login({ serverStatus, openLogin, isLoggingIn }) {
  const [open, setOpen] = useState(true);
  const { signInWithGoogle, signInWithEmailPassword } = useAuthContext();
  const { respLogin, setRespLogin } = useWSContext();

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [BlockInputText, setBlockInputText] = useState(false);

  const [alertInfo, setAlertInfo] = useState({
    active: false,
    severity: 'info',
    title: '',
    message: '',
  });

  const LoginSchema = Yup.object().shape({
    user: Yup.string()
      .email('Formato de e-mail inválido')
      .matches(
        /^[a-zA-Z0-9._%+-]+@tecnomotor\.com\.br$/,
        'Use um e-mail da Tecnomotor'
      )
      .required('Email é obrigatório'),
    password: Yup.string().required('A senha é obrigatória'),
  });

  const formik = useFormik({
    initialValues: {
      user: '',
      password: '',
      remember: true,
    },
    validationSchema: LoginSchema,
    onSubmit: async (values) => {
      if (loading) return;

      setAlertInfo({ active: false });
      setLoading(true);
      try {
        await signInWithEmailPassword(values.user, values.password);
      } catch (err) {
        setLoading(false);
        if (err.message === 'EMAIL_NOT_VERIFIED') {
          setAlertInfo({
            active: true,
            severity: 'warning',
            title: 'Verifique seu E-mail!',
            message:
              'Sua conta ainda não foi verificada. Um novo e-mail foi enviado para sua caixa de entrada.',
          });
        } else {
          setAlertInfo({
            active: true,
            severity: 'error',
            title: 'Usuário ou senha inválido!',
            message: 'Aguarde e tente novamente.',
          });
        }
      }
    },
  });

  function erroLogin() {
    if (respLogin === 'true') {
      setTimeout(() => {
        setLoading(false);
        setOpen(false);
        setRespLogin(null);
      }, 3000);
    } else if (respLogin === ' USER_OR_PASS_INCORRECT') {
      setLoading(false);
      setBlockInputText(true);
      setAlertInfo({
        active: true,
        severity: 'error',
        title: 'Usuário ou senha inválido!',
        message: 'Aguarde e tente novamente.',
      });
      setTimeout(() => {
        setAlertInfo({ active: false });
        setRespLogin(null);
        setBlockInputText(false);
      }, 1500);
    } else if (respLogin === ' USER_IS_CONECTED') {
      setLoading(false);
      setBlockInputText(true);
      setAlertInfo({
        active: true,
        severity: 'error',
        title: 'Usuário já está conectado!',
        message: 'Aguarde e tente novamente.',
      });
      setTimeout(() => {
        setAlertInfo({ active: false });
        setRespLogin(null);
        setBlockInputText(false);
      }, 1500);
    } else if (respLogin === null) {
      setAlertInfo({ active: false });
    }
  }

  useEffect(erroLogin, [respLogin]);

  const { errors, touched, handleSubmit, getFieldProps } = formik;
  const handleShowPassword = () => setShowPassword((show) => !show);

  useEffect(() => {
    notification();
  }, []);

  async function handleSignInGoogle() {
    if (loading) return;
    setAlertInfo({ active: false });
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setLoading(false);
      setAlertInfo({
        active: true,
        severity: 'error',
        title: 'Erro ao fazer login',
        message: 'Tente novamente.',
      });
    }
  }

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open || openLogin}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      sx={{ display: 'flex', width: '100%', background: '#444', height: '100vh' }}
    >
      <Fade in={open || openLogin}>
        <RootStyle title="Login | Suporte">
          <PerfectScrollbar options={{ suppressScrollX: true }}>
            <SectionStyle>
              {/* HEADER DE VERSÃO AQUI */}
              <LoginHeader />
              <Typography variant="h1" sx={{ textAlign: 'center', pt: 2.5 }}>
                Tecnozap
              </Typography>
              <Typography variant="h3" sx={{ textAlign: 'center' }}>
                Apoio Técnico
              </Typography>
              <Typography variant="h6" sx={{ textAlign: 'center', m: 1 }}>
                Insira nos campos abaixo seu login para começar seus atendimentos.
              </Typography>
            </SectionStyle>
            <Container maxWidth="sm">
              <ContentStyle>
                <Stack sx={{ mb: 5 }}>
                  <Typography variant="h4" gutterBottom sx={{ textAlign: 'center' }}>
                    Login Suporte
                  </Typography>
                </Stack>
                <FormikProvider value={formik}>
                  <Form autoComplete="on" noValidate onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      <TextField
                        fullWidth
                        autoComplete="username"
                        type="text"
                        label="Email"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiInputBase-input:-webkit-autofill': {
                            WebkitBoxShadow: (theme) =>
                              `0 0 0 100px ${theme.palette.background.paper} inset`,
                            WebkitTextFillColor: (theme) =>
                              theme.palette.text.primary,
                          },
                        }}
                        disabled={
                          BlockInputText || serverStatus.status !== 'connected'
                        }
                        {...getFieldProps('user')}
                        error={Boolean(touched.user && errors.user)}
                        helperText={touched.user && errors.user}
                      />

                      <TextField
                        fullWidth
                        autoComplete="current-password"
                        type={showPassword ? 'text' : 'password'}
                        label="Senha"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiInputBase-input:-webkit-autofill': {
                            WebkitBoxShadow: (theme) =>
                              `0 0 0 100px ${theme.palette.background.paper} inset`,
                            WebkitTextFillColor: (theme) =>
                              theme.palette.text.primary,
                          },
                        }}
                        disabled={
                          BlockInputText || serverStatus.status !== 'connected'
                        }
                        {...getFieldProps('password')}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={handleShowPassword} edge="end">
                                <Iconify
                                  icon={
                                    showPassword
                                      ? 'eva:eye-fill'
                                      : 'eva:eye-off-fill'
                                  }
                                />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        error={Boolean(touched.password && errors.password)}
                        helperText={touched.password && errors.password}
                      />
                    </Stack>
                    <MotionContainer initial="initial" open>
                      <motion.div variants={varBounceIn}>
                        {alertInfo.active && (
                          <Box sx={{ mt: 2, mb: 1 }}>
                            <Alert
                              variant="filled"
                              severity={alertInfo.severity}
                              sx={{ width: '100%' }}
                            >
                              <AlertTitle>{alertInfo.title}</AlertTitle>
                              {alertInfo.message}
                            </Alert>
                          </Box>
                        )}
                      </motion.div>
                    </MotionContainer>
                    <LoadingButton
                      disabled={serverStatus.status !== 'connected'}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading || isLoggingIn}
                      sx={{ mt: 2 }}
                    >
                      Login
                    </LoadingButton>
                    <Box sx={{ mt: 0 }}>
                      <LoadingButton
                        startIcon={<Icon icon="flat-color-icons:google" />}
                        fullWidth
                        onClick={() => handleSignInGoogle()}
                        variant="contained"
                        color="inherit"
                        size="large"
                        sx={{ mt: 3 }}
                        disabled={
                          loading ||
                          isLoggingIn ||
                          serverStatus.status !== 'connected'
                        }
                      >
                        Login com o Google
                      </LoadingButton>
                    </Box>
                  </Form>
                </FormikProvider>
              </ContentStyle>
            </Container>
          </PerfectScrollbar>
        </RootStyle>
      </Fade>
    </Modal>
  );
}

export default Login;
